<template>
  <div class="flex w-full h-full bg-neutral-100 gap-4">
    <div
      v-if="uiFlags.isFetchingItem"
      class="flex items-center justify-center w-full text-base"
    >
      <spinner size="" />
      <span class="ml-2">{{ $t('CONTACT_PROFILE.LOADING') }}</span>
    </div>

    <template v-else-if="contact.id">
      <div
        class="w-[350px] flex-shrink-0 bg-neutral-50 shadow-[4px_0px_4px_0px_rgba(163,163,163,0.1)] rtl:shadow-[-4px_0px_4px_0px_rgba(163,163,163,0.1)] flex flex-col h-full overflow-hidden"
      >
        <div class="h-[24px] flex-shrink-0" />

        <div class="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div class="flex flex-col gap-[8px] flex-shrink-0">
            <div class="flex flex-row items-center gap-[8px] px-[20px]">
              <thumbnail
                :src="contact.thumbnail"
                :username="contact.name"
                size="64px"
              />
              <h2
                class="text-[20px] font-bold leading-[24px] text-gray-600 m-0 flex items-center gap-1 min-w-0"
              >
                <span class="truncate">{{ contact.name }}</span>
                <v2-icon v-if="contact.identifier" icon="info" :size="16" />
              </h2>
            </div>

            <div
              v-if="additionalAttributes.description"
              class="px-[20px] text-[14px] leading-[20px] text-gray-600"
            >
              <p class="m-0" :class="{ 'line-clamp-3': !bioExpanded }">
                {{ additionalAttributes.description }}
              </p>
              <button
                v-if="
                  additionalAttributes.description &&
                  additionalAttributes.description.length > 120
                "
                class="text-brand text-[14px] leading-[20px] mt-1 cursor-pointer bg-transparent border-0 p-0"
                @click="bioExpanded = !bioExpanded"
              >
                {{
                  bioExpanded
                    ? $t('CONTACT_PROFILE.READ_LESS')
                    : $t('CONTACT_PROFILE.READ_MORE')
                }}
                <v2-icon
                  v-if="bioExpanded"
                  icon="keyboard_arrow_up"
                  class="align-middle"
                  size="16"
                />
                <v2-icon
                  v-else
                  icon="keyboard_arrow_down"
                  class="align-middle"
                  size="16"
                />
              </button>
            </div>

            <div class="flex flex-col gap-[18px] pb-[8px] pt-[8px] px-[20px]">
              <div class="flex flex-col gap-[10px]">
                <div class="grid grid-cols-2 gap-x-[20px] gap-y-[12px]">
                  <div
                    v-if="contact.email"
                    class="flex items-center gap-[8px] text-gray-600 min-w-0"
                  >
                    <v2-icon
                      icon="alternate_email"
                      :size="16"
                      class="flex-shrink-0"
                    />
                    <span class="text-[12px] leading-[18px] truncate">{{
                      contact.email
                    }}</span>
                  </div>
                  <div
                    v-if="
                      additionalAttributes.city || additionalAttributes.country
                    "
                    class="flex items-center gap-[8px] text-gray-600 min-w-0"
                  >
                    <v2-icon icon="location" size="16" class="flex-shrink-0" />
                    <span class="text-[12px] leading-[18px] truncate">
                      {{
                        [
                          additionalAttributes.city,
                          additionalAttributes.country,
                        ]
                          .filter(Boolean)
                          .join(', ')
                      }}
                    </span>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-x-[20px] gap-y-[12px]">
                  <div
                    v-if="contact.phone_number"
                    class="flex items-center gap-[8px] text-gray-600 min-w-0"
                  >
                    <v2-icon
                      icon="phone_enabled"
                      size="16"
                      class="flex-shrink-0 -scale-x-100"
                    />
                    <span class="text-[12px] leading-[18px]" dir="ltr">{{
                      contact.phone_number
                    }}</span>
                  </div>
                  <div
                    v-if="contact.created_at"
                    class="flex items-center gap-[8px] text-gray-600 min-w-0"
                  >
                    <v2-icon
                      icon="calendar_month"
                      size="16"
                      class="flex-shrink-0"
                    />
                    <span class="text-[12px] leading-[18px]">{{
                      formattedCreatedAt
                    }}</span>
                  </div>
                </div>
                <div
                  v-if="additionalAttributes.company_name"
                  class="flex items-center gap-[8px] text-gray-600"
                >
                  <v2-icon
                    icon="office_building"
                    size="16"
                    class="flex-shrink-0"
                  />
                  <span class="text-[12px] leading-[18px] truncate">{{
                    additionalAttributes.company_name
                  }}</span>
                </div>
              </div>

              <div
                v-if="hasSocialProfiles"
                class="flex items-center gap-[8px] -mt-[10px]"
              >
                <a
                  v-if="socialProfiles.facebook"
                  :href="`https://facebook.com/${socialProfiles.facebook}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="social-icon-link"
                >
                  <v2-icon icon="facebook" size="24" />
                </a>
                <a
                  v-if="socialProfiles.linkedin"
                  :href="`https://linkedin.com/in/${socialProfiles.linkedin}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="social-icon-link"
                >
                  <v2-icon icon="linkedin" size="24" />
                </a>
                <a
                  v-if="socialProfiles.instagram"
                  :href="`https://instagram.com/${socialProfiles.instagram}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="social-icon-link"
                >
                  <v2-icon icon="instagram" size="24" />
                </a>
                <a
                  v-if="socialProfiles.twitter"
                  :href="`https://x.com/${socialProfiles.twitter}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="social-icon-link"
                >
                  <v2-icon icon="twitter" size="24" />
                </a>
              </div>

              <button-v2
                variant="filled"
                size="sm"
                icon-left="send"
                full-width
                @click="toggleConversationModal"
              >
                {{ $t('CONTACT_PANEL.NEW_MESSAGE') }}
              </button-v2>
            </div>
          </div>

          <div class="flex border-b border-gray-300 flex-shrink-0">
            <button
              v-for="tab in sidebarTabs"
              :key="tab.key"
              :class="
                activeSidebarTab === tab.key
                  ? 'px-[10px] pt-[10px] pb-[7px] border-b-[3px] border-solid border-gray-600 font-semibold text-gray-600'
                  : 'px-[5px] py-[10px] text-gray-500'
              "
              class="flex-1 text-center whitespace-nowrap text-[14px] leading-[20px] bg-transparent border-0 cursor-pointer"
              @click="activeSidebarTab = tab.key"
            >
              {{ tab.name }}
            </button>
          </div>

          <div
            class="flex flex-col justify-between flex-1 overflow-auto gap-3 px-4 py-2"
          >
            <template v-if="activeSidebarTab === 'custom_fields'">
              <div class="flex-1 overflow-auto">
                <custom-attributes
                  :contact-id="contact.id"
                  attribute-type="contact_attribute"
                  attribute-class="conversation--attribute !bg-white !border-0"
                  :custom-attributes="contact.custom_attributes"
                  class="even"
                />
              </div>
              <div class="w-full text-center">
                <custom-attribute-selector
                  attribute-type="contact_attribute"
                  :contact-id="contact.id"
                  :ui-version="'v2'"
                />
              </div>
            </template>
            <contact-conversations
              v-else-if="activeSidebarTab === 'conversations'"
              :contact-id="contact.id"
              conversation-id=""
            />
            <contact-private-files
              v-else-if="activeSidebarTab === 'files'"
              :contact-id="Number(contactId)"
            />
          </div>
        </div>

        <div
          class="flex items-center justify-center gap-[8px] px-[12px] pt-[16px] pb-[20px] flex-shrink-0"
        >
          <button
            v-if="hasEditPermission('Contacts')"
            v-tooltip="$t('EDIT_CONTACT.BUTTON_LABEL')"
            class="w-[32px] h-[32px] bg-white rounded-[4px] flex items-center justify-center flex-shrink-0 cursor-pointer border border-solid border-gray-200 shadow-[0px_1px_8px_0px_rgba(221,221,221,0.25)] hover:bg-gray-50 transition-colors"
            @click="toggleEditModal"
          >
            <v2-icon icon="edit" size="16" />
          </button>
          <button
            v-tooltip="$t('CONTACT_PROFILE.ADD_TO_LIST')"
            class="w-[32px] h-[32px] bg-white rounded-[4px] flex items-center justify-center flex-shrink-0 cursor-pointer border border-solid border-gray-200 shadow-[0px_1px_8px_0px_rgba(221,221,221,0.25)] hover:bg-gray-50 transition-colors"
            @click="showAddToListModal = true"
          >
            <v2-icon icon="playlist_add" size="16" />
          </button>
          <button
            v-tooltip="$t('NOTES.HEADER.TITLE')"
            class="w-[32px] h-[32px] bg-white rounded-[4px] flex items-center justify-center flex-shrink-0 cursor-pointer border border-solid border-gray-200 shadow-[0px_1px_8px_0px_rgba(221,221,221,0.25)] hover:bg-gray-50 transition-colors"
            @click="navigateToNotes"
          >
            <v2-icon icon="note" size="16" />
          </button>
          <button
            v-if="canEditContact"
            v-tooltip="$t('CONTACT_PANEL.MERGE_CONTACT')"
            class="w-[32px] h-[32px] bg-white rounded-[4px] flex items-center justify-center flex-shrink-0 cursor-pointer border border-solid border-gray-200 shadow-[0px_1px_8px_0px_rgba(221,221,221,0.25)] hover:bg-gray-50 transition-colors"
            :disabled="uiFlags.isMerging"
            @click="toggleMergeModal"
          >
            <v2-icon icon="merge" size="16" />
          </button>
          <button
            v-if="hasEditPermission('Contacts')"
            v-tooltip="$t('CONTACT_PANEL.BLOCK_CONTACT')"
            class="w-[32px] h-[32px] bg-white rounded-[4px] flex items-center justify-center flex-shrink-0 cursor-pointer border border-solid border-gray-200 shadow-[0px_1px_8px_0px_rgba(221,221,221,0.25)] hover:bg-gray-50 transition-colors"
            @click="handleBlockToggle"
          >
            <v2-icon icon="volume_mute" size="16" class="text-gray-500" />
          </button>
          <button
            v-if="hasDeletePermission('Contacts')"
            v-tooltip="$t('DELETE_CONTACT.CONFIRM.TITLE')"
            class="w-[32px] h-[32px] bg-red-50 rounded-[4px] flex items-center justify-center flex-shrink-0 cursor-pointer border border-solid border-red-100 shadow-[0px_1px_8px_0px_rgba(221,221,221,0.25)] hover:bg-red-100 transition-colors"
            @click="toggleDeleteModal"
          >
            <v2-icon icon="delete" size="16" class="text-red-600" />
          </button>
        </div>
      </div>

      <div
        class="flex-1 flex flex-col overflow-hidden pt-4 pr-5 rtl:pl-5 rtl:pr-0"
      >
        <div class="flex items-center gap-[2px] flex-shrink-0 mb-4">
          <template v-if="isListContext">
            <router-link
              :to="listsUrl"
              class="text-[14px] leading-[20px] text-brand p-1 hover:underline"
            >
              {{ $t('LISTS_PAGE.HEADER') }}
            </router-link>
            <v2-icon
              icon="keyboard_arrow_right"
              class="text-gray-400"
              :class="{ 'rtl-flip': isRTL }"
            />
            <router-link
              :to="listMembersUrl"
              class="text-[14px] leading-[20px] text-brand p-1 hover:underline inline-block truncate max-w-[200px] align-bottom"
            >
              {{ listName }}
            </router-link>
            <v2-icon
              icon="keyboard_arrow_right"
              class="text-gray-400"
              :class="{ 'rtl-flip': isRTL }"
            />
            <span class="text-[14px] leading-[20px] text-gray-600 p-1">{{
              contact.name
            }}</span>
          </template>
          <template v-else>
            <router-link
              :to="backUrl"
              class="text-[14px] leading-[20px] text-brand p-1 hover:underline"
            >
              {{ $t('CONTACTS_PAGE.HEADER') }}
            </router-link>
            <v2-icon
              icon="keyboard_arrow_right"
              class="text-gray-400"
              :class="{ 'rtl-flip': isRTL }"
            />
            <span class="text-[14px] leading-[20px] text-gray-600 p-1">{{
              contact.name
            }}</span>
          </template>
        </div>

        <div
          class="flex w-fit border-b border-gray-300 gap-[40px] flex-shrink-0 mb-6"
        >
          <button
            v-for="(tab, index) in tabs"
            :key="tab.key"
            :class="
              activeTabKey === tab.key
                ? 'px-[10px] pt-[10px] pb-[7px] border-b-[3px] border-solid border-gray-600 font-bold text-[14px] text-gray-600'
                : 'px-[5px] py-[10px] text-[14px] text-gray-500'
            "
            class="bg-transparent border-0 cursor-pointer leading-[18px]"
            @click="onClickTabChange(index)"
          >
            {{ tab.name }}
          </button>
        </div>

        <div class="flex-1 overflow-auto">
          <contact-notes
            v-if="activeTabKey === 0"
            :contact-id="Number(contactId)"
          />
          <contact-private-messages
            v-if="activeTabKey === 1"
            :contact-id="Number(contactId)"
          />
          <contact-private-attachments
            v-if="activeTabKey === 2"
            :contact-id="Number(contactId)"
          />
          <contact-private-files
            v-if="activeTabKey === 3"
            :contact-id="Number(contactId)"
          />
        </div>
      </div>
    </template>

    <edit-contact
      v-if="showEditModal && canEditContact"
      :show="showEditModal"
      :contact="contact"
      @cancel="toggleEditModal"
    />
    <new-conversation
      v-if="contact.id"
      :show="showConversationModal"
      :contact="contact"
      @cancel="toggleConversationModal"
    />
    <contact-merge-modal
      v-if="showMergeModal && canEditContact"
      :primary-contact="contact"
      :show="showMergeModal"
      @close="toggleMergeModal"
    />
    <woot-delete-modal
      v-if="showDeleteModal && hasDeletePermission('Contacts')"
      :show.sync="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
    <block-contact-modal ref="confirmDialog" @change="onChangeBlockDuration" />
    <woot-modal
      class="add-to-list-modal"
      :show.sync="showAddToListModal"
      :on-close="() => (showAddToListModal = false)"
    >
      <add-contact-to-list-modal
        v-if="showAddToListModal"
        :contact-id="contact.id"
        @close="showAddToListModal = false"
      />
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ContactNotes from 'dashboard/modules/notes/NotesOnContactPage.vue';
import ContactPrivateMessages from 'dashboard/modules/PrivateMessages/PrivateMessagesOnContactPage';
import ContactPrivateAttachments from 'dashboard/modules/PrivateMessages/PrivateAttachmentsOnContactPage';
import ContactPrivateFiles from 'dashboard/modules/PrivateMessages/PrivateFilesOnContactPage';
import Spinner from 'shared/components/Spinner.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import V2Icon from 'v2/components/Icon.vue';
import ButtonV2 from 'v2/components/Button/ButtonV2.vue';
import EditContact from 'dashboard/routes/dashboard/conversation/contact/EditContact.vue';
import NewConversation from 'dashboard/routes/dashboard/conversation/contact/NewConversation.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import BlockContactModal from 'dashboard/routes/dashboard/conversation/contact/BlockContactModal.vue';
import AddContactToListModal from 'dashboard/routes/dashboard/contacts/components/AddContactToListModal.vue';
import CustomAttributes from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributes.vue';
import CustomAttributeSelector from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributeSelector.vue';
import ContactConversations from 'dashboard/routes/dashboard/conversation/ContactConversations.vue';
import alertMixin from 'shared/mixins/alertMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';

export default {
  components: {
    ContactNotes,
    ContactPrivateMessages,
    ContactPrivateAttachments,
    ContactPrivateFiles,
    Spinner,
    Thumbnail,
    V2Icon,
    ButtonV2,
    EditContact,
    NewConversation,
    ContactMergeModal,
    BlockContactModal,
    AddContactToListModal,
    CustomAttributes,
    CustomAttributeSelector,
    ContactConversations,
  },
  mixins: [alertMixin, hasPermissionMixin],
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
    listId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      selectedTabIndex: 0,
      activeTabKey: 0,
      activeSidebarTab: 'custom_fields',
      showEditModal: false,
      showConversationModal: false,
      bioExpanded: false,
      showMergeModal: false,
      showDeleteModal: false,
      showAddToListModal: false,
      blockDuration: null,
    };
  },
  computed: {
    isRTL() {
      return this.$root.$i18n.locale === 'he';
    },
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      getAccount: 'accounts/getAccount',
      accountId: 'getCurrentAccountId',
      currentList: 'lists/getCurrentList',
    }),
    isListContext() {
      return this.$route.name === 'list_member_profile';
    },
    listName() {
      return this.currentList?.name || '';
    },
    listMembersUrl() {
      return {
        name: 'list_members',
        params: { accountId: this.$route.params.accountId, id: this.listId },
      };
    },
    listsUrl() {
      return {
        name: 'list_dashboard',
        params: { accountId: this.$route.params.accountId },
      };
    },
    tabs() {
      const tabs = [
        {
          key: 1,
          name: this.$t('PRIVATE_MESSAGES.HEADER.TITLE'),
        },
      ];
      if (!this.newRulesSystemEnabled || this.hasEditPermission('Contacts')) {
        tabs.unshift({
          key: 0,
          name: this.$t('NOTES.HEADER.TITLE'),
        });
      }
      if (this.hasReadPermission('Contacts')) {
        tabs.push(
          {
            key: 2,
            name: this.$t('ATTACH_FILES.HEADER.TITLE'),
          },
          {
            key: 3,
            name: this.$t('FILES.HEADER.TITLE'),
          }
        );
      }
      return tabs;
    },
    sidebarTabs() {
      return [
        {
          key: 'custom_fields',
          name: this.$t('CONTACT_PROFILE.SIDEBAR_TABS.CUSTOM_FIELDS'),
        },
        {
          key: 'conversations',
          name: this.$t('CONTACT_PROFILE.SIDEBAR_TABS.CONVERSATIONS'),
        },
        {
          key: 'files',
          name: this.$t('CONTACT_PROFILE.SIDEBAR_TABS.FILES'),
        },
      ];
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    backUrl() {
      return {
        name: 'contacts_dashboard',
        params: { accountId: this.$route.params.accountId },
      };
    },
    account() {
      return this.getAccount(this.accountId);
    },
    newRulesSystemEnabled() {
      return this.account?.features?.new_rules_system;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    socialProfiles() {
      return this.additionalAttributes.social_profiles || {};
    },
    hasSocialProfiles() {
      return Object.values(this.socialProfiles).some(Boolean);
    },
    formattedCreatedAt() {
      if (!this.contact.created_at) return '';
      const date = new Date(this.contact.created_at * 1000);
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${dd}.${mm}.${yyyy}`;
    },
    contactBlocked() {
      return this.contact?.blocked;
    },
    canEditContact() {
      return !this.newRulesSystemEnabled || this.hasEditPermission('Contacts');
    },
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  watch: {
    selectedTabIndex(index) {
      this.activeTabKey = this.tabs[index].key;
    },
  },
  mounted() {
    this.fetchContactDetails();
    this.activeTabKey = this.tabs[0].key;
    if (this.isListContext && this.listId) {
      this.$store.dispatch('lists/show', this.listId);
    }
  },
  methods: {
    onClickTabChange(index) {
      this.selectedTabIndex = index;
      this.activeTabKey = this.tabs[index].key;
    },
    fetchContactDetails() {
      const { contactId: id } = this;
      this.$store.dispatch('contacts/show', { id });
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    toggleConversationModal() {
      this.showConversationModal = !this.showConversationModal;
    },
    toggleMergeModal() {
      this.showMergeModal = !this.showMergeModal;
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    closeDelete() {
      this.showDeleteModal = false;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('contacts/delete', this.contact.id);
        this.showAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));
        this.$router.push({ name: 'contacts_dashboard' });
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    async handleBlockToggle() {
      if (this.contactBlocked) {
        await this.$store.dispatch('contacts/unblockContact', {
          contactId: this.contact.id,
          conversationId: null,
        });
        this.showAlert(this.$t('CONTACT_PANEL.UNBLOCKED_SUCCESS'));
      } else {
        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok && this.blockDuration !== null) {
          await this.$store.dispatch('contacts/blockContact', {
            contactId: this.contact.id,
            conversationId: null,
            period: this.blockDuration.id,
          });
          this.showAlert(this.$t('CONTACT_PANEL.BLOCKED_SUCCESS'));
        }
      }
    },
    onChangeBlockDuration(duration) {
      this.blockDuration = duration;
    },
    navigateToNotes() {
      const notesTabIndex = this.tabs.findIndex(tab => tab.key === 0);
      if (notesTabIndex !== -1) {
        this.onClickTabChange(notesTabIndex);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.add-to-list-modal ::v-deep .modal-container {
  width: 554px;
  max-width: calc(100vw - 32px);
  border-radius: 12px;
}

.rtl-flip {
  transform: scaleX(-1);
}

.social-icon-link {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  filter: grayscale(1);
  transition:
    filter 0.2s,
    opacity 0.2s;

  &:hover {
    filter: grayscale(0);
    opacity: 0.8;
  }
}
</style>
